import { ExternalLink } from 'lucide-react';

import type { ExtractionStats } from '~/lib/loaders/load-extraction-stats';

export type { ExtractionStats };

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface FeatureItem {
  title: string;
  description: React.ReactNode;
}

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

const CHPL_REGISTRY_URL =
  'https://www.gov.pl/web/urpl/rejestr-produktow-leczniczych3';

const EU_REGISTER_URL =
  'https://ec.europa.eu/health/documents/community-register/html/index_en.htm';

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/** Format a date string to Polish locale: "9 lutego 2026, 13:29" */
export function formatSyncDate(isoDate: string): string {
  const date = new Date(isoDate);

  const datePart = date.toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const timePart = date.toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${datePart}, ${timePart}`;
}

/* -------------------------------------------------------------------------- */
/*  Feature items builder                                                     */
/* -------------------------------------------------------------------------- */

export function buildFeatureItems(
  extractionStats: ExtractionStats,
  lastSyncAt?: string | null,
): FeatureItem[] {
  const extractedPercentage =
    extractionStats.totalCount > 0
      ? (
          (extractionStats.extractedCount / extractionStats.totalCount) *
          100
        ).toFixed(1)
      : '0.0';

  const cenCount = extractionStats.cenCount.toLocaleString('pl-PL');
  const cenPercentage =
    extractionStats.totalCount > 0
      ? Math.round(
          (extractionStats.cenCount / extractionStats.totalCount) * 100,
        )
      : 0;

  const syncText = (
    <>
      Codziennie synchronizujemy naszą bazę z{' '}
      <a
        href={CHPL_REGISTRY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="underline decoration-slate-300 underline-offset-2 transition-colors hover:text-slate-700 hover:decoration-slate-400 dark:decoration-slate-600 dark:hover:text-slate-100 dark:hover:decoration-slate-400"
      >
        Rejestrem Produktów Leczniczych
        <ExternalLink className="mb-0.5 ml-0.5 inline h-3 w-3" />
      </a>{' '}
      - nowe leki, wycofania i zmiany w charakterystykach.
    </>
  );

  return [
    {
      title: 'Codzienna aktualizacja z RPL',
      description: lastSyncAt ? (
        <>
          {syncText} Ostatnia aktualizacja:{' '}
          <strong className="font-semibold text-slate-600 dark:text-slate-200">
            {formatSyncDate(lastSyncAt)}.
          </strong>
        </>
      ) : (
        syncText
      ),
    },
    {
      title: 'Brakujące leki z rejestru unijnego',
      description: (
        <>
          {cenCount} leków ({cenPercentage}% bazy) nie posiada ChPL ani ulotki w
          RPL. Wyodrębniamy je z oficjalnej dokumentacji{' '}
          <a
            href={EU_REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 underline decoration-slate-300 underline-offset-2 transition-colors hover:text-slate-700 hover:decoration-slate-400 dark:decoration-slate-600 dark:hover:text-slate-100 dark:hover:decoration-slate-400"
          >
            Rejestru Unijnego
            <ExternalLink className="inline h-3 w-3" />
          </a>
          . LEKolizja to jedyny serwis w Polsce z pełną bazą.
        </>
      ),
    },
    {
      title: 'Średnio 22 sekundy',
      description: 'Tyle trwa analiza pełnego zestawu leków.',
    },
    {
      title: `${extractionStats.extractedCount.toLocaleString('pl-PL')} leków w bazie`,
      description: `${extractedPercentage}% leków zarejestrowanych w Polsce, na receptę i bez recepty (OTC).`,
    },
    {
      title: 'Do 15 leków jednocześnie',
      description:
        'Sprawdź interakcje między nawet 15 lekami na raz. Liczba leków zależy od planu.',
    },
    {
      title: 'Wielopoziomowa analiza interakcji',
      description:
        'Nie tylko nazwa leku - szukamy połączeń także m.in. po substancji czynnej, klasie farmakologicznej czy mechanizmie działania.',
    },
  ];
}
